"use client";

import React, { useEffect, useState } from "react";
import { CircleCheck as CheckCircle2, ChevronRight, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
interface BreadcrumbItem {
  name: string;
  url: string;
}
export default function InteriorRemodelingHero() {
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [separator, setSeparator] = useState("›");
  useEffect(() => {
    let cancelled = false;
    getWvcClient()?.pluginRequest("wvc-seo/v1/breadcrumbs", {
      params: {
        url: "/WholeHomeInteriorRemodelingPage"
      },
      cache: true
    }).then((data: {
      items: BreadcrumbItem[];
      separator: string;
    }) => {
      if (!cancelled && data?.items) {
        setBreadcrumbs(data.items);
        if (data.separator) setSeparator(data.separator);
      }
    }).catch(() => {
      if (!cancelled) setBreadcrumbs([]);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const inclusions = [{
    title: "Hardwood & Luxury Flooring",
    detail: "Wide-plank site-finished hardwoods, engineered options, tile, and seamless threshold transitions across all living spaces."
  }, {
    title: "Architectural Trim & Millwork",
    detail: "Custom coffered ceilings, crown molding, baseboards, wainscoting, and tailored built-in cabinetry."
  }, {
    title: "Paint & Surface Finishes",
    detail: "Level 5 drywall finishing, premium low-VOC primers and designer coatings across all ceilings, walls, and trim."
  }, {
    title: "Architectural & Task Lighting",
    detail: "Recessed ambient LEDs, statement pendants, cove illumination, and smart whole-home dimmer automation."
  }, {
    title: "Open-Concept Conversions",
    detail: "Strategic structural modifications uniting living, kitchen, and dining zones into functional flowing layouts."
  }, {
    title: "Room Reconfiguration & Structural Framing",
    detail: "Load-bearing beam insertions, engineered header installation, and custom floor plan adjustments."
  }];
  return <section id="interiorremodelinghero" data-nav="light" className="relative bg-background text-foreground pt-12 pb-16 lg:pt-16 lg:pb-24 overflow-hidden border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {breadcrumbs.length > 1 && <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-muted-foreground font-default">
              {breadcrumbs.map((crumb, idx) => <li key={idx} className="flex items-center gap-2">
                  {idx < breadcrumbs.length - 1 && crumb.url ? <Link to={crumb.url} className="hover:text-foreground transition-colors">
                      {crumb.name}
                    </Link> : <span aria-current="page" className="text-foreground font-medium">
                      {crumb.name}
                    </span>}
                  {idx < breadcrumbs.length - 1 && <span aria-hidden="true" className="text-muted-foreground/60">
                      {separator}
                    </span>}
                </li>)}
            </ol>
          </nav>}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="space-y-4">
              <span className="text-xs uppercase tracking-wider font-semibold text-primary">
                Brookhaven, Georgia Remodeling
              </span>
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-tight">
                Whole-home interior remodeling engineered for Brookhaven homes.
              </h1>
              <p className="text-base sm:text-lg text-foreground/80 leading-relaxed font-default pt-2">
                Interior remodeling in Brookhaven transforms outdated floor plans into cohesive, light-filled living spaces built for modern daily life. Brookhaven Elevation Renovations manages the entire construction lifecycle, from structural load-bearing beam removal to artisanal millwork and finish paint. Every renovation is tailored to respect the distinct architectural character of historic Brookhaven, Ashford Park, and Drew Valley residences.
              </p>
            </div>

            <div className="mt-8 flex flex-wrap gap-4 items-center">
              <Button asChild size="lg" className="rounded-md shadow-sm">
                <Link to="/ContactRequestAnEstimatePage">
                  Request an estimate
                  <ArrowRight className="size-4 ml-2" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-md">
                <Link to="/OurProjectsPage">
                  View completed projects
                </Link>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden border border-border shadow-lg bg-card">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_interior_renovation.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_interior_renovation.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_interior_renovation.png-wvc-sizes" alt="Bright open-concept living space with hardwood flooring, coffered ceiling, and recessed lighting in Brookhaven" loading="eager" className="w-full h-80 sm:h-96 lg:h-[460px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-4 left-4 right-4 p-4 rounded-lg bg-background/90 backdrop-blur-xs border border-border/80 text-foreground">
                <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                  Historic & Modern Homes
                </p>
                <p className="text-sm text-foreground/90 font-medium mt-0.5">
                  Complete interior transformations with dedicated project oversight.
                </p>
              </div>
            </div>
          </div>
        </div>

        <Separator className="my-12 lg:my-16" />

        <div className="space-y-6">
          <div className="max-w-2xl">
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
              What&apos;s Included in an Interior Remodel
            </h2>
            <p className="text-muted-foreground text-sm sm:text-base mt-2">
              Our comprehensive interior scope addresses every foundational and aesthetic surface to ensure lasting structural integrity and architectural cohesion.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {inclusions.map((item, index) => <Card key={index} data-index={index} className="border border-border bg-card/70 hover:bg-card hover:shadow-md transition-all duration-200">
                <CardContent className="p-5 flex flex-col gap-2">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-base font-semibold text-foreground tracking-tight">
                        {item.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1.5 leading-normal">
                        {item.detail}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </div>
    </section>;
}